import { styled } from '@mui/system';
import { Box, Button, IconButton } from '@mui/material';

import { SectionHeading, ContentContainer } from '../../styles';
import { colors } from '../../../../../constants/theme';

export const PAGINATION_DOT_SIZE_PX = 10;
export const PAGINATION_DOT_GAP_PX = 8;
export const PAGINATION_DOT_STEP_PX = PAGINATION_DOT_SIZE_PX + PAGINATION_DOT_GAP_PX;

export const TestimonialsContentContainer = styled(ContentContainer)({
  alignItems: 'start',
  maxWidth: '1100px',
  width: '100%',
  position: 'relative',
});

export const TestimonialsHeading = styled(SectionHeading)(({ theme }) => ({
  textAlign: 'left',
  margin: '0 auto',
  color: colors.primary,
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
  },
}));

export const TestimonialWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '670px',
  display: 'flex',
  flexDirection: 'row',
  gap: '30px',
  maxWidth: '1100px',
  margin: '60px auto 0',
  overflow: 'hidden',
  outline: 'none',
  borderRadius: '16px',

  [theme.breakpoints.down('lg')]: {
    minHeight: '565px',
    maxWidth: '900px',
  },
  [theme.breakpoints.down('md')]: {
    minHeight: '400px',
    maxWidth: '700px',
  },
  [theme.breakpoints.down(768)]: {
    maxWidth: '300px',
  },

  '&:focus': {
    outline: '2px solid rgba(51, 38, 29, 0.2)',
    outlineOffset: '4px',
  },

  '& .slick-slider': {
    width: '100%',
    height: '100%',
  },

  '& .slick-list': {
    marginBottom: 0,
    overflowY: 'visible',
  },

  '& .slick-track': {
    display: 'flex',
    alignItems: 'center',
    height: '580px',
    [theme.breakpoints.down('lg')]: {
      height: '490px',
    },
    [theme.breakpoints.down('md')]: {
      height: '339px',
    },
  },

  '& .slick-slide': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'all 0.3s ease-in-out',
    outline: 'none !important',

    '& > div': {
      display: 'flex',
      justifyContent: 'center',
      outline: 'none',
    },
  },

  '& .slick-current': {
    overflow: 'visible',
  },
}));

export const TestimonialSliderColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minWidth: 0,
});

export const PaginationDots = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignSelf: 'center',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    marginTop: '48px',
  },
  [theme.breakpoints.down('md')]: {
    marginTop: '38px',
  },
}));

export const PaginationDotsViewport = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'viewportWidthPx',
})(({ viewportWidthPx }) => ({
  overflow: 'hidden',
  flexShrink: 0,
  minHeight: '15px',
  alignContent: 'center',
  ...(viewportWidthPx > 0 && { width: `${viewportWidthPx}px` }),
}));

export const PaginationDotsTrack = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'translateXPx',
})(({ translateXPx = 0 }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: `${PAGINATION_DOT_GAP_PX}px`,
  willChange: 'transform',
  transform: `translateX(-${translateXPx}px)`,
  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  minHeight: 10,
}));

export const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  width: `${PAGINATION_DOT_SIZE_PX}px`,
  height: `${PAGINATION_DOT_SIZE_PX}px`,
  minWidth: `${PAGINATION_DOT_SIZE_PX}px`,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  backgroundColor: active ? colors.primary : '#e2e1df',
  cursor: 'pointer',
  transition: 'background-color 0.3s, transform 0.3s',
  flexShrink: 0,
  transform: active ? 'scale(1.2)' : 'none',
  '&:hover': {
    backgroundColor: active ? '#2c2c2c' : '#999',
  },
  '&:focus-visible': {
    outline: '2px solid rgba(51, 38, 29, 0.35)',
    outlineOffset: '2px',
  },
}));

export const TestimonialNavigationButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '20px',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const NavigationIconButton = styled(IconButton)({
  backgroundColor: '#E6EBF0',
  color: '#2c2c2c',
  padding: '12px',
  '&:hover': {
    backgroundColor: '#d0d5da',
  },
});

export const SliderNavigationButton = styled(Button)({
  position: 'absolute',
  background: '#FFF',
  borderRadius: '50%',
  padding: '0 !important',
  minWidth: '48px !important',
  height: '48px !important',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  zIndex: 10,
  '&:hover': {
    background: '#f5f5f5',
  },
});

export const SliderNavigationButtonLeft = styled(SliderNavigationButton)(({ theme }) => ({
  left: '-100px',
  [theme.breakpoints.down('1300')]: {
    left: '10px',
  },
}));

export const SliderNavigationButtonRight = styled(SliderNavigationButton)(({ theme }) => ({
  right: '-100px',
  [theme.breakpoints.down('1300')]: {
    right: '10px',
  },
}));
